import * as THREE from "three";

import type { WorldSnapshot } from "../simulation/snapshot";
import { TURRET_TELEGRAPH_EARLY_S } from "../simulation/constants";
import { sampleHeight } from "../world/terrain";
import { createTerrainAnnulus, updateTerrainAnnulus } from "./terrain-annulus";

const GROUND_TELEGRAPH_CLEARANCE_M = 1.2;

type LineEntry = { line: THREE.Line; active: boolean };
type RingEntry = {
  mesh: THREE.Mesh;
  cross: THREE.Line;
  active: boolean;
};

/** Pooled 3D telegraphs and hazard zones from simulation snapshot. */
export class TelegraphRenderer {
  private readonly group = new THREE.Group();
  private readonly linePool: LineEntry[] = [];
  private readonly ringPool: RingEntry[] = [];
  private readonly enemyById = new Map<number, WorldSnapshot["enemies"][number]>();
  private readonly maxLines = 12;
  private readonly maxRings = 16;
  private colorblind = false;

  public constructor(parent: THREE.Scene) {
    parent.add(this.group);
    for (let i = 0; i < this.maxLines; i += 1) {
      this.linePool.push(this.makeLine(0xff4444, 0.85));
    }
    for (let i = 0; i < this.maxRings; i += 1) {
      const mesh = createTerrainAnnulus({ color: 0xff3333, opacity: 0.55 });
      mesh.visible = false;
      mesh.frustumCulled = false;
      const crossGeom = new THREE.BufferGeometry();
      crossGeom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(12), 3));
      const cross = new THREE.LineSegments(
        crossGeom,
        new THREE.LineBasicMaterial({
          color: 0xffaa44,
          transparent: true,
          opacity: 0.9,
          depthTest: false,
        }),
      );
      cross.visible = false;
      cross.frustumCulled = false;
      this.group.add(mesh);
      this.group.add(cross);
      this.ringPool.push({ mesh, cross, active: false });
    }
  }

  public sync(snap: WorldSnapshot, enemies: WorldSnapshot["enemies"]): void {
    this.colorblind = snap.settings.colorblindTelegraphs;
    for (const entry of this.linePool) entry.active = false;
    for (const entry of this.ringPool) entry.active = false;

    let lineIdx = 0;
    let ringIdx = 0;

    this.enemyById.clear();
    for (const enemy of enemies) this.enemyById.set(enemy.id, enemy);

    for (const telegraph of snap.telegraphs) {
      if (telegraph.kind === "turret_aim") {
        const enemy = this.enemyById.get(telegraph.enemyId);
        if (!enemy || lineIdx >= this.maxLines) continue;
        const entry = this.linePool[lineIdx]!;
        lineIdx += 1;
        this.setLine(
          entry,
          enemy.x,
          enemy.altitude + 0.5,
          enemy.y,
          telegraph.x,
          sampleHeight(snap.seed, telegraph.x, telegraph.y) + GROUND_TELEGRAPH_CLEARANCE_M,
          telegraph.y,
        );
        const mat = entry.line.material as THREE.LineBasicMaterial;
        // Normalize against the longest telegraph so early-game cues still ramp cleanly.
        const progress = Math.max(0, 1 - telegraph.life / TURRET_TELEGRAPH_EARLY_S);
        mat.opacity = 0.35 + progress * 0.55;
        mat.color.setHex(this.colorblind ? 0x44ccff : 0xff4444);
        entry.active = true;
      } else if (telegraph.kind === "mine" && ringIdx < this.maxRings) {
        const entry = this.ringPool[ringIdx]!;
        ringIdx += 1;
        this.setRing(
          entry,
          snap.seed,
          telegraph.x,
          telegraph.y,
          2.8,
          telegraph.life <= 1 ? 1.15 : 0.85,
          true,
        );
        entry.active = true;
      }
    }

    for (const hazard of snap.hazards) {
      if (hazard.kind !== "mine" || ringIdx >= this.maxRings) continue;
      const entry = this.ringPool[ringIdx]!;
      ringIdx += 1;
      const pulse = hazard.life < 1.2 ? 1.25 : 1;
      this.setRing(
        entry,
        snap.seed,
        hazard.x,
        hazard.y,
        hazard.radius * pulse,
        hazard.life / 4,
        false,
      );
      const mat = entry.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = hazard.life < 1 ? 0.75 : 0.45;
      entry.active = true;
    }

    for (const entry of this.linePool) entry.line.visible = entry.active;
    for (const entry of this.ringPool) {
      entry.mesh.visible = entry.active;
      entry.cross.visible = entry.active && this.colorblind;
    }
  }

  public dispose(): void {
    this.group.removeFromParent();
    for (const { line } of this.linePool) {
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    }
    for (const { mesh, cross } of this.ringPool) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      cross.geometry.dispose();
      (cross.material as THREE.Material).dispose();
    }
  }

  private makeLine(color: number, opacity: number): LineEntry {
    const line = this.makeBareLine(color, opacity);
    this.group.add(line);
    return { line, active: false };
  }

  private makeBareLine(color: number, opacity: number): THREE.Line {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
    const line = new THREE.Line(
      geom,
      new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthTest: false,
      }),
    );
    line.visible = false;
    line.frustumCulled = false;
    return line;
  }

  private setLine(
    entry: LineEntry,
    x0: number,
    y0: number,
    z0: number,
    x1: number,
    y1: number,
    z1: number,
  ): void {
    this.setBareLine(entry.line, x0, y0, z0, x1, y1, z1);
  }

  private setBareLine(
    line: THREE.Line,
    x0: number,
    y0: number,
    z0: number,
    x1: number,
    y1: number,
    z1: number,
  ): void {
    const pos = line.geometry.getAttribute("position") as THREE.BufferAttribute;
    pos.setXYZ(0, x0, y0, z0);
    pos.setXYZ(1, x1, y1, z1);
    pos.needsUpdate = true;
  }

  private setRing(
    entry: RingEntry,
    seed: number,
    x: number,
    z: number,
    radius: number,
    innerScale: number,
    isTelegraph: boolean,
  ): void {
    updateTerrainAnnulus(entry.mesh as THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>, {
      seed,
      centerX: x,
      centerZ: z,
      radius,
      thickness: 0.3,
    });
    const mat = entry.mesh.material as THREE.MeshBasicMaterial;
    if (this.colorblind) {
      mat.color.setHex(isTelegraph ? 0xffaa44 : 0xff8800);
    } else {
      mat.color.setHex(innerScale > 1 ? 0xff2222 : 0xff6644);
    }

    const crossPos = entry.cross.geometry.getAttribute("position") as THREE.BufferAttribute;
    const arm = radius * 0.55;
    crossPos.setXYZ(0, x - arm, sampleHeight(seed, x - arm, z) + 0.82, z);
    crossPos.setXYZ(1, x + arm, sampleHeight(seed, x + arm, z) + 0.82, z);
    crossPos.setXYZ(2, x, sampleHeight(seed, x, z - arm) + 0.82, z - arm);
    crossPos.setXYZ(3, x, sampleHeight(seed, x, z + arm) + 0.82, z + arm);
    crossPos.needsUpdate = true;
  }
}
